import React, { useState, useEffect, useRef } from 'react';
import Picker from 'react-mobile-picker';
import { bool, func, string, oneOfType, ReactElement } from 'prop-types';

import {
  Container,
  TopRow,
  LeftSection,
  RightSection,
  PickerValue,
  StyledPickerWrapper,
  Label,
  Checkbox,
} from './styles';

const parseTimeString = (timeStr, is12Hours) => {
  if (!timeStr) {
    return is12Hours
      ? { hour: '12', minute: '00', period: 'PM' }
      : { hour: '12', minute: '00' };
  }

  // Remove any non-numeric suffix (like 'am' or 'pm')
  const match = timeStr.match(/^(\d{1,2}):(\d{2})\s?(am|pm|AM|PM)?$/);
  if (!match) {
    // fallback to default
    return is12Hours
      ? { hour: '12', minute: '00', period: 'PM' }
      : { hour: '12', minute: '00' };
  }

  let [, hour, minute, period] = match;
  hour = parseInt(hour, 10);

  if (is12Hours) {
    let p = period ? period.toUpperCase() : null;
    let h = hour;

    if (!p) {
      if (h === 0) {
        h = 12;
        p = 'AM';
      } else if (h === 12) {
        p = 'PM';
      } else if (h > 12) {
        h -= 12;
        p = 'PM';
      } else {
        p = 'AM';
      }
    } else if (h === 0) {
      h = 12;
    }

    return {
      hour: String(h).padStart(2, '0'),
      minute: minute.padStart(2, '0'),
      period: p,
    };
  } else {
    return {
      hour: String(hour).padStart(2, '0'),
      minute: minute.padStart(2, '0'),
    };
  }
};

const CustomTimePicker = ({
  id,
  currentValue,
  handleTimeChange,
  activePickerId,
  handlePickerClick,
  label,
  is12Hours,
  isActive,
  handleActiveChange,
  hideTimePicker = false,
}) => {
  const wheelThrottleRef = useRef(0);
  const pickerWrapperRef = useRef(null);

  const hours = Array.from({ length: is12Hours ? 12 : 24 }, (_, i) =>
    String(is12Hours ? i + 1 : i).padStart(2, '0')
  );
  const minutes = Array.from({ length: 60 }, (_, i) =>
    i.toString().padStart(2, '0')
  );
  const periods = ['AM', 'PM'];

  const showCheckbox =
    typeof isActive === 'boolean' && typeof handleActiveChange === 'function';

  const optionGroups = {
    hour: hours,
    minute: minutes,
  };
  if (is12Hours) {
    optionGroups.period = periods;
  }

  const initialPickerValue =
    typeof currentValue === 'string'
      ? parseTimeString(currentValue, is12Hours)
      : currentValue ||
      (is12Hours
        ? { hour: '12', minute: '00', period: 'PM' }
        : { hour: '00', minute: '00' });

  const getTimeString = (val) => {
    const { hour, minute, period } = val;
    if (is12Hours) {
      let h = parseInt(hour, 10);
      if (period === 'PM' && h !== 12) h += 12;
      if (period === 'AM' && h === 12) h = 0;
      return `${String(h).padStart(2, '0')}:${minute}`;
    }
    return `${hour}:${minute}`;
  };

  const [pickerValue, setPickerValue] = useState(initialPickerValue);

  // Update pickerValue only if currentValue changed from outside
  useEffect(() => {
    const newValue =
      typeof currentValue === 'string'
        ? parseTimeString(currentValue, is12Hours)
        : currentValue ||
        (is12Hours
          ? { hour: '12', minute: '00', period: 'PM' }
          : { hour: '00', minute: '00' });

    setPickerValue(newValue);
  }, [currentValue, is12Hours]);

  useEffect(() => {
    if (currentValue === '') {
      handleTimeChange('12:00');
    }
  }, [currentValue, is12Hours, handleTimeChange]);

  useEffect(() => {
    const el = pickerWrapperRef.current;
    if (!el) {
      return;
    }
    el.addEventListener('wheel', handleWheelCapture, { passive: false });
    return () => el.removeEventListener('wheel', handleWheelCapture);
  }, [activePickerId, pickerValue]);

  // Format for display
  const formattedTime = is12Hours
    ? `${pickerValue.hour}:${pickerValue.minute} ${pickerValue.period}`
    : `${pickerValue.hour}:${pickerValue.minute}`;

  // When picker changes, update both pickerValue and lastExternalValue
  const handlePickerChange = (name, displayValue) => {
    setPickerValue((prev) => {
      const newValue = { ...prev, [name]: displayValue };
      handleTimeChange(getTimeString(newValue));
      return newValue;
    });
  };

  const stepPickerColumn = (columnName, direction) => {
    if (!columnName || !optionGroups[columnName]) {
      return;
    }

    const options = optionGroups[columnName];
    const current = pickerValue[columnName];
    const currentIndex = options.indexOf(current);
    if (currentIndex < 0) {
      return;
    }

    const nextIndex =
      (currentIndex + direction + options.length) % options.length;
    handlePickerChange(columnName, options[nextIndex]);
  };

  const handleWheelCapture = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const MIN_INTERVAL_MS = 150;
    const MIN_DELTA = 2;
    const now = Date.now();
    const deltaY = event.deltaY || 0;
    const delta = Math.abs(deltaY);

    if (delta < MIN_DELTA || now - wheelThrottleRef.current < MIN_INTERVAL_MS) {
      return;
    }

    const pickerColumnElement = event.target.closest('.picker-column');
    if (!pickerColumnElement) {
      return;
    }

    const allColumns = pickerColumnElement.parentElement
      ? Array.from(
        pickerColumnElement.parentElement.querySelectorAll('.picker-column')
      )
      : [];
    const columnIndex = allColumns.indexOf(pickerColumnElement);
    const columnKeys = Object.keys(optionGroups);
    const columnName = columnKeys[columnIndex];
    const direction = deltaY > 0 ? 1 : -1;

    stepPickerColumn(columnName, direction);
    wheelThrottleRef.current = now;
  };

  const renderRightSection = () => {
    if (showCheckbox && !isActive || hideTimePicker) {
      return null;
    }

    return (
      <PickerValue onClick={(e) => handlePickerClick(id, e)}>
        {formattedTime}
      </PickerValue>
    );
  };

  return (
    <Container key={id}>
      <TopRow>
        <TopRow>
          <LeftSection>
            {showCheckbox && (
              <Checkbox
                onChange={handleActiveChange}
                checked={isActive} />
            )}
            <Label isActive={isActive}>{label}</Label>
          </LeftSection>
          <RightSection>{renderRightSection()}</RightSection>
        </TopRow>
      </TopRow>
      {activePickerId === id && (
        <StyledPickerWrapper ref={pickerWrapperRef}>
          <Picker
            optionGroups={optionGroups}
            valueGroups={pickerValue}
            onChange={handlePickerChange}
            height={150}
            itemHeight={30}
          />
        </StyledPickerWrapper>
      )}
    </Container>
  );
};

CustomTimePicker.propTypes = {
  id: string.isRequired,
  currentValue: string,
  handleTimeChange: func.isRequired,
  activePickerId: string,
  handlePickerClick: func.isRequired,
  label: oneOfType([string, ReactElement]),
  is12Hours: bool,
  isActive: bool,
  handleActiveChange: func,
  hideTimePicker: bool,
};

CustomTimePicker.defaultProps = {
  currentValue: '',
  activePickerId: null,
  label: '',
  is12Hours: false,
};

export default CustomTimePicker;
